import { useState, useCallback } from 'react';
import { Coin, CoinType, PowerUp, PowerUpType } from './types';
import { v4 as uuidv4 } from 'uuid';

const COIN_CONFIG = {
  bronze: { value: 1, probability: 0.5, size: 35 },
  silver: { value: 2, probability: 0.3, size: 40 },
  gold: { value: 3, probability: 0.15, size: 45 },
  gem: { value: 5, probability: 0.04, size: 50 },
  chest: { value: 10, probability: 0.01, size: 55 },
};

const PATTERNS = ['linear', 'circular', 'zigzag'] as const;
const MIN_COINS = 25; 
const MAX_COINS = 30; 

export const useCoinsGeneration = () => {
  const [coins, setCoins] = useState<Coin[]>([]);
  const [powerUps, setPowerUps] = useState<PowerUp[]>([]);

  const generateRandomCoinType = (): CoinType => {
    const random = Math.random();
    let cumulativeProbability = 0;
    
    for (const [type, config] of Object.entries(COIN_CONFIG)) {
      cumulativeProbability += config.probability;
      if (random <= cumulativeProbability) {
        return type as CoinType;
      }
    }
    
    return 'bronze';
  };

  const generateRandomPattern = () => {
    const random = Math.random();
    if (random < 0.4) return 'linear';
    if (random < 0.8) return 'zigzag';
    return 'circular';
  };

  const generateCoin = useCallback((index: number): Coin => {
    const type = generateRandomCoinType();
    const config = COIN_CONFIG[type];
    const pattern = generateRandomPattern();
    
    const containerWidth = window.innerWidth * 0.8;
    const containerHeight = 400;
    const margin = 20; 

    const gridCols = 6;
    const gridRows = 5;
    const cellWidth = (containerWidth - margin * 2) / gridCols;
    const cellHeight = (containerHeight - margin * 2) / gridRows;

    const gridX = index % gridCols;
    const gridY = Math.floor(index / gridCols) % gridRows;

    const randomOffset = 0.6; 
    const x = margin + (cellWidth * gridX) + (Math.random() * randomOffset - randomOffset/2) * cellWidth;
    const y = margin + (cellHeight * gridY) + (Math.random() * randomOffset - randomOffset/2) * cellHeight;

    const baseSpeed = type === 'bronze' ? 2 :
                     type === 'silver' ? 2.5 :
                     type === 'gold' ? 3 :
                     type === 'gem' ? 3.5 :
                     4; 

    return {
      id: uuidv4(),
      x,
      y,
      size: config.size,
      value: config.value,
      type,
      collected: false,
      speed: baseSpeed + Math.random(), 
      direction: Math.random() * Math.PI * 2,
      pattern,
    };
  }, []);

  const generatePowerUp = useCallback((): PowerUp => {
    const types: PowerUpType[] = ['magnet', 'multiplier', 'slowdown'];
    const type = types[Math.floor(Math.random() * types.length)];
    
    return {
      id: uuidv4(),
      x: Math.random() * (window.innerWidth * 0.8 - 40),
      y: Math.random() * (400 - 40),
      type,
      collected: false,
      duration: 5,
    };
  }, []);

  const generateCoins = useCallback(() => {
    const numberOfCoins = MIN_COINS + Math.floor(Math.random() * (MAX_COINS - MIN_COINS + 1));
    const newCoins = Array.from({ length: numberOfCoins }, (_, i) => generateCoin(i));
    setCoins(newCoins);

    if (Math.random() < 0.3) { 
      setPowerUps([generatePowerUp()]);
    } else {
      setPowerUps([]);
    }
  }, [generateCoin]);

  return {
    coins,
    setCoins,
    powerUps,
    setPowerUps,
    generateCoins,
  };
};